/**
 * 我的页 — 翻译自 user/pages/user-index/user-index.vue
 *
 * 阶段 2：mock 数据 + 静态布局
 * 阶段 4：接 /user/info、/common/friendApps
 */
"use client";

import { useRouter } from "next/navigation";
import {
  ChevronRight,
  ClipboardList,
  MapPin,
  Ticket,
  Megaphone,
  Bike,
  Heart,
  MessageSquare,
  LogOut,
  Smartphone,
  Lock,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useAppStore } from "@/lib/store";

export default function MyPage() {
  const router = useRouter();
  const { userInfo } = useAppStore();
  const isLogin = !!userInfo.token;

  return (
    <div className="min-h-screen bg-page-common-bg pb-20">
      {/* ============ 顶部背景 ============ */}
      <div
        className="relative bg-main text-white"
        style={{
          height: "225px",
          backgroundImage: "url(/static/user/bg1.png)",
          backgroundSize: "cover",
          backgroundPosition: "center",
          borderRadius: "0 0 30px 30px",
        }}
      />

      {/* ============ 用户卡片（与原版 margin-top: -230rpx）============ */}
      <div className="px-4 -mt-44 relative z-10">
        {/* 头像 + 昵称 */}
        <div
          className="flex items-center gap-3 mb-4"
          onClick={() => {
            if (!isLogin) router.push("/login");
          }}
        >
          <div className="w-15 h-15 rounded-full bg-white border-2 border-white overflow-hidden flex items-center justify-center">
            {isLogin && userInfo.avatar ? (
              // eslint-disable-next-line @next/next/no-img-element
              <img
                src={userInfo.avatar}
                alt="avatar"
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full bg-secondary flex items-center justify-center text-text-secondary">
                👤
              </div>
            )}
          </div>
          <div className="text-white">
            <div className="text-lg font-medium">
              {isLogin ? userInfo.nickname : "请登录"}
            </div>
            {isLogin ? (
              <div className="text-xs opacity-90 flex items-center gap-2 mt-1">
                <Smartphone size={12} />
                <span>{userInfo.mobile}</span>
              </div>
            ) : (
              <div className="text-xs opacity-90 mt-1">登录后享受更多服务</div>
            )}
          </div>
        </div>

        {/* 主列表：我的订单 / 地址簿 / 优惠券 / 公告 */}
        <Card className="p-2 mt-2">
          <div className="grid grid-cols-4 gap-1">
            {MAIN_LIST.map((item) => (
              <button
                key={item.title}
                onClick={() => item.path && router.push(item.path)}
                className="flex flex-col items-center justify-center py-3 rounded-lg hover:bg-secondary transition-colors"
              >
                <div className="w-10 h-10 rounded-full bg-main-bg text-main flex items-center justify-center mb-1">
                  <item.icon size={20} />
                </div>
                <span className="text-xs tn-text-primary truncate">
                  {item.title}
                </span>
              </button>
            ))}
          </div>
        </Card>

        {/* 应用服务（骑手入驻 + 关注 + 反馈） */}
        <Card className="p-2 mt-3">
          <div className="px-3 py-2 text-base font-bold tn-text-primary">
            应用服务
          </div>
          <div className="grid grid-cols-4 gap-1 p-2">
            {APP_LIST.map((item) => (
              <button
                key={item.title}
                onClick={() => item.path && router.push(item.path)}
                className={appItemClass(item.color)}
                title={item.title}
              >
                <div
                  className={iconClass(item.color)}
                  style={{
                    backgroundImage:
                      "url(/static/user/icon_bg.png)",
                  }}
                >
                  <item.icon size={24} />
                </div>
                <span className="text-xs tn-text-primary mt-2 truncate">
                  {item.title}
                </span>
              </button>
            ))}
          </div>
        </Card>

        {/* 关于我们列表 */}
        <Card className="mt-3 overflow-hidden">
          {ABOUT_LIST.map((item, i) => (
            <button
              key={item.title}
              onClick={() => item.path && router.push(item.path)}
              className={`w-full flex items-center gap-3 px-4 py-3 ${
                i !== ABOUT_LIST.length - 1 ? "border-b border-border" : ""
              }`}
            >
              <div className={iconClass(item.color)}>
                <item.icon size={20} />
              </div>
              <span className="flex-1 text-left text-base">{item.title}</span>
              <ChevronRight size={18} className="text-text-secondary" />
            </button>
          ))}
        </Card>

        {/* 退出登录 */}
        {isLogin && (
          <Button
            variant="outline"
            block
            className="mt-6"
            size="lg"
            onClick={() => {
              if (confirm("是否退出登录？")) {
                useAppStore.getState().clearUserInfo();
                localStorage.removeItem("xiadanuser_jwt");
              }
            }}
          >
            <LogOut size={18} />
            退出登录
          </Button>
        )}
      </div>
    </div>
  );
}

// ===== 数据定义 =====

const MAIN_LIST = [
  { title: "我的订单", icon: ClipboardList, path: "/run/order-list" },
  { title: "地址簿", icon: MapPin, path: "/my/address" },
  { title: "优惠券", icon: Ticket, path: "/my/coupon" },
  { title: "公告列表", icon: Megaphone, path: "/my/notice" },
  { title: "修改密码", icon: Lock, path: "/my/change-password" },
];

const APP_LIST = [
  { title: "骑手入驻", icon: Bike, color: "blue", path: "/my/rider-apply" },
  { title: "关注我们", icon: Heart, color: "red", path: "" },
  { title: "问题反馈", icon: MessageSquare, color: "orangered", path: "" },
  { title: "充值", icon: Smartphone, color: "green", path: "/my/recharge" },
];

const ABOUT_LIST = [
  { title: "合作加盟", icon: ClipboardList, color: "cool-2", path: "" },
  { title: "关于我们", icon: MessageSquare, color: "cool-5", path: "" },
  { title: "联系客服", icon: MessageSquare, color: "cool-9", path: "" },
];

// ===== 工具类 =====

function appItemClass(_color: string) {
  return "flex flex-col items-center justify-center py-2 rounded-lg hover:bg-secondary transition-colors cursor-pointer";
}

function iconClass(color: string) {
  const colorMap: Record<string, string> = {
    blue: "from-blue-400 to-blue-600",
    red: "from-red-400 to-red-600",
    orangered: "from-orange-500 to-red-600",
    green: "from-green-400 to-green-600",
    orange: "from-orange-400 to-orange-600",
    "cool-2": "from-cyan-400 to-cyan-600",
    "cool-5": "from-violet-400 to-violet-600",
    "cool-9": "from-fuchsia-400 to-fuchsia-600",
  };
  const gradient = colorMap[color] || "from-gray-400 to-gray-600";
  return `w-12 h-12 rounded-full bg-gradient-to-br ${gradient} text-white flex items-center justify-center relative z-10`;
}
